import {
  permissionConstants,
  POLICY_ACTIONS,
  POLICY_SCOPES,
} from "@/src/constants/permissionConstants.js";
import { isABACEnabled } from "@/src/utils/dynamicFormUtils.js";
import { canPerformAction } from "@/src/utils/abacPermissionUtils.js";
import AuthPermissionService from "@/src/api/AuthPermissionService.js";

export default {
  namespaced: true,
  state: {
    permissions: [],
    hasAllAccess: false,
    hasUsersAccess: false,
    hasBillingAccess: false,
    hasIntegrationsAccess: false,
    hasObjectsAccess: false,
    hasChatsAccess: false,
    hasCallSettingsAccess: false,
    hasEmailTemplatesAccess: false,
    hasDealPipelineAccess: false,
    hasSharedEmailsAccess: false,
    hasSharedLinkedinAccess: false,
    hasImportAccess: false,
    hasExportAccess: false,
    hasCustomViewsAccess: false,
    hasProspectLeadsAccess: false,
    hasProspectCompaniesAccess: false,
    hasAiSdrAccess: false,
    hasSequencesAccess: false,
    hasDialerAccess: false,
    hasConversationsAccess: false,
    hasMeetingsAccess: false,
    hasContactsAccess: false,
    hasCompaniesAccess: false,
    hasLeadsAccess: false,
    hasDealsAccess: false,
    hasListsAccess: false,
    hasAnalyticsAccess: false,
    hasTasksAccess: false,
    hasAllTasksAccess: false,
    hasEmailsAccess: false,
    hasCampaignsInEngageAccess: false,
    hasWaterfallAccess: false,
    hasAgentBuilderAccess: false,
    hasBulkDeleteAccess: false,
    hasWorkflowsAccess: false,
    hasHrAttendanceAccess: false,
    hasHrEmployeeMgmtAccess: false,
    hasHrShiftsAccess: false,
    hasHrLeaveRequestsAccess: false,
    hasHrLeaveApprovalsAccess: false,
    hasHrLeaveTypesAccess: false,
    hasHrHolidaysAccess: false,
    hasHrPerformanceAccess: false,
    hasHrOkrsAccess: false,
    hasHrGoalsAccess: false,
    hasHrEmployeesAccess: false,
    hasHrDepartmentsAccess: false,
    hasHrTeamsAccess: false,
    hasHrRolesAccess: false,
    hasHrOnboardingAccess: false,
    hasHrOffboardingAccess: false,
    hasHrRequisitionsAccess: false,
    hasHrJobOpeningsAccess: false,
    hasHrCandidatesAccess: false,
    hasHrCampaignsAccess: false,
    userPolicies: [],
    isABACMode: false,
    currentRoleId: null,
    isPoliciesFetching: false,
  },
  mutations: {
    SET_PERMISSIONS(state, permissions) {
      state.permissions = permissions;
    },
    SET_ALL_ACCESS(state, hasAccess) {
      state.hasAllAccess = hasAccess;
    },
    SET_USERS_ACCESS(state, hasAccess) {
      state.hasUsersAccess = hasAccess;
    },
    SET_BILLING_ACCESS(state, hasAccess) {
      state.hasBillingAccess = hasAccess;
    },
    SET_INTEGRATIONS_ACCESS(state, hasAccess) {
      state.hasIntegrationsAccess = hasAccess;
    },
    SET_OBJECTS_ACCESS(state, hasAccess) {
      state.hasObjectsAccess = hasAccess;
    },
    SET_CHATS_ACCESS(state, hasAccess) {
      state.hasChatsAccess = hasAccess;
    },
    SET_CALL_SETTINGS_ACCESS(state, hasAccess) {
      state.hasCallSettingsAccess = hasAccess;
    },
    SET_EMAIL_TEMPLATES_ACCESS(state, hasAccess) {
      state.hasEmailTemplatesAccess = hasAccess;
    },
    SET_DEAL_PIPELINE_ACCESS(state, hasAccess) {
      state.hasDealPipelineAccess = hasAccess;
    },
    SET_SHARED_EMAILS_ACCESS(state, hasAccess) {
      state.hasSharedEmailsAccess = hasAccess;
    },
    SET_SHARED_LINKEDIN_ACCESS(state, hasAccess) {
      state.hasSharedLinkedinAccess = hasAccess;
    },
    SET_IMPORT_ACCESS(state, hasAccess) {
      state.hasImportAccess = hasAccess;
    },
    SET_EXPORT_ACCESS(state, hasAccess) {
      state.hasExportAccess = hasAccess;
    },
    SET_CUSTOM_VIEWS_ACCESS(state, hasAccess) {
      state.hasCustomViewsAccess = hasAccess;
    },
    SET_PROSPECT_LEADS_ACCESS(state, hasAccess) {
      state.hasProspectLeadsAccess = hasAccess;
    },
    SET_PROSPECT_COMPANIES_ACCESS(state, hasAccess) {
      state.hasProspectCompaniesAccess = hasAccess;
    },
    SET_AI_SDR_ACCESS(state, hasAccess) {
      state.hasAiSdrAccess = hasAccess;
    },
    SET_SEQUENCES_ACCESS(state, hasAccess) {
      state.hasSequencesAccess = hasAccess;
    },
    SET_WORKFLOWS_ACCESS(state, hasAccess) {
      state.hasWorkflowsAccess = hasAccess;
    },
    SET_HR_ATTENDANCE_ACCESS(state, hasAccess) {
      state.hasHrAttendanceAccess = hasAccess;
    },
    SET_HR_EMPLOYEE_MGMT_ACCESS(state, hasAccess) {
      state.hasHrEmployeeMgmtAccess = hasAccess;
    },
    SET_HR_SHIFTS_ACCESS(state, hasAccess) {
      state.hasHrShiftsAccess = hasAccess;
    },
    SET_HR_LEAVE_REQUESTS_ACCESS(state, hasAccess) {
      state.hasHrLeaveRequestsAccess = hasAccess;
    },
    SET_HR_LEAVE_APPROVALS_ACCESS(state, hasAccess) {
      state.hasHrLeaveApprovalsAccess = hasAccess;
    },
    SET_HR_LEAVE_TYPES_ACCESS(state, hasAccess) {
      state.hasHrLeaveTypesAccess = hasAccess;
    },
    SET_HR_HOLIDAYS_ACCESS(state, hasAccess) {
      state.hasHrHolidaysAccess = hasAccess;
    },
    SET_HR_PERFORMANCE_ACCESS(state, hasAccess) {
      state.hasHrPerformanceAccess = hasAccess;
    },
    SET_HR_OKRS_ACCESS(state, hasAccess) {
      state.hasHrOkrsAccess = hasAccess;
    },
    SET_HR_GOALS_ACCESS(state, hasAccess) {
      state.hasHrGoalsAccess = hasAccess;
    },
    SET_HR_EMPLOYEES_ACCESS(state, hasAccess) {
      state.hasHrEmployeesAccess = hasAccess;
    },
    SET_HR_DEPARTMENTS_ACCESS(state, hasAccess) {
      state.hasHrDepartmentsAccess = hasAccess;
    },
    SET_HR_TEAMS_ACCESS(state, hasAccess) {
      state.hasHrTeamsAccess = hasAccess;
    },
    SET_HR_ROLES_ACCESS(state, hasAccess) {
      state.hasHrRolesAccess = hasAccess;
    },
    SET_HR_ONBOARDING_ACCESS(state, hasAccess) {
      state.hasHrOnboardingAccess = hasAccess;
    },
    SET_HR_OFFBOARDING_ACCESS(state, hasAccess) {
      state.hasHrOffboardingAccess = hasAccess;
    },
    SET_HR_REQUISITIONS_ACCESS(state, hasAccess) {
      state.hasHrRequisitionsAccess = hasAccess;
    },
    SET_HR_JOB_OPENINGS_ACCESS(state, hasAccess) {
      state.hasHrJobOpeningsAccess = hasAccess;
    },
    SET_HR_CANDIDATES_ACCESS(state, hasAccess) {
      state.hasHrCandidatesAccess = hasAccess;
    },
    SET_HR_CAMPAIGNS_ACCESS(state, hasAccess) {
      state.hasHrCampaignsAccess = hasAccess;
    },
    SET_DIALER_ACCESS(state, hasAccess) {
      state.hasDialerAccess = hasAccess;
    },
    SET_CONVERSATIONS_ACCESS(state, hasAccess) {
      state.hasConversationsAccess = hasAccess;
    },
    SET_MEETINGS_ACCESS(state, hasAccess) {
      state.hasMeetingsAccess = hasAccess;
    },
    SET_CONTACTS_ACCESS(state, hasAccess) {
      state.hasContactsAccess = hasAccess;
    },
    SET_COMPANIES_ACCESS(state, hasAccess) {
      state.hasCompaniesAccess = hasAccess;
    },
    SET_LEADS_ACCESS(state, hasAccess) {
      state.hasLeadsAccess = hasAccess;
    },
    SET_DEALS_ACCESS(state, hasAccess) {
      state.hasDealsAccess = hasAccess;
    },
    SET_LISTS_ACCESS(state, hasAccess) {
      state.hasListsAccess = hasAccess;
    },
    SET_ANALYTICS_ACCESS(state, hasAccess) {
      state.hasAnalyticsAccess = hasAccess;
    },
    SET_TASKS_ACCESS(state, hasAccess) {
      state.hasTasksAccess = hasAccess;
    },
    SET_ALL_TASKS_ACCESS(state, hasAccess) {
      state.hasAllTasksAccess = hasAccess;
    },
    SET_EMAILS_ACCESS(state, hasAccess) {
      state.hasEmailsAccess = hasAccess;
    },
    SET_CAMPAIGNS_IN_ENGAGE_ACCESS(state, hasAccess) {
      state.hasCampaignsInEngageAccess = hasAccess;
    },
    SET_WATERFALL_ACCESS(state, hasAccess) {
      state.hasWaterfallAccess = hasAccess;
    },
    SET_AGENT_BUILDER_ACCESS(state, hasAccess) {
      state.hasAgentBuilderAccess = hasAccess;
    },
    SET_BULK_DELETE_ACCESS(state, hasAccess) {
      state.hasBulkDeleteAccess = hasAccess;
    },
    SET_USER_POLICIES(state, policies) {
      state.userPolicies = policies;
    },
    SET_ABAC_MODE(state, isEnabled) {
      state.isABACMode = isEnabled;
    },
    SET_CURRENT_ROLE_ID(state, roleId) {
      state.currentRoleId = roleId;
    },
    SET_POLICIES_FETCHING(state, isFetching) {
      state.isPoliciesFetching = isFetching;
    },
  },
  actions: {
    async fetchABACPolicies({ commit, state }, { force = false } = {}) {
      if (!force && state.isPoliciesFetching) {
        return;
      }

      commit("SET_POLICIES_FETCHING", true);

      try {
        const userId = localStorage.getItem("userId");

        if (!userId) {
          console.error("[ABAC Store] No user ID found in localStorage");
          return;
        }

        const membershipResponse =
          await AuthPermissionService.getUserRoleMembership(userId);

        if (!membershipResponse?.data?.attributes?.role_id) {
          console.error("[ABAC Store] No role membership found for user");

          return;
        }

        const roleId = membershipResponse.data.attributes.role_id;

        if (
          !force &&
          state.userPolicies.length > 0 &&
          state.currentRoleId === roleId
        ) {
          return;
        }

        const response =
          await AuthPermissionService.fetchPoliciesByRole(roleId);

        if (!response?.data) {
          console.error("[ABAC Store] Failed to fetch policies");
          return;
        }

        const policies = response.data.map((item) => ({
          resource: item.attributes.resource_id,
          action: item.attributes.action,
          scope: item.attributes.scope,
          condition: item.attributes.condition || null,
          role_id: item.attributes.role_id,
        }));

        commit("SET_USER_POLICIES", policies);
        commit("SET_ABAC_MODE", true);
        commit("SET_CURRENT_ROLE_ID", roleId);

        if (policies.length === 0) {
          console.warn("⚠️ [ABAC Store] No policies found for this role!");
          console.warn(
            "⚠️ [ABAC Store] Go to Permission Management to assign permissions."
          );
        }

        const hasViewAccess = (resourceName) => {
          return policies.some(
            (p) =>
              p.resource.toLowerCase() === resourceName.toLowerCase() &&
              (p.action.toUpperCase() === POLICY_ACTIONS.VIEW ||
                p.action.toUpperCase() === POLICY_ACTIONS.ENABLED) &&
              p.scope !== POLICY_SCOPES.NONE_OF_THE_RECORDS
          );
        };

        const permissionsSet = [];

        Object.entries(permissionConstants).forEach(([key, value]) => {
          if (value === permissionConstants.ALL) return;
          if (
            value === permissionConstants.IMPORT_ACCESS ||
            value === permissionConstants.EXPORT_ACCESS
          )
            return;

          const mutationName = `SET_${key}_ACCESS`;
          const hasAccess = hasViewAccess(value);

          try {
            commit(mutationName, hasAccess);
            permissionsSet.push({
              resource: value,
              mutation: mutationName,
              hasAccess,
            });
          } catch (e) {
            console.warn(`[ABAC Store] Mutation not found: ${mutationName}`);
          }
        });

        commit(
          "SET_IMPORT_ACCESS",
          policies.some(
            (p) =>
              p.resource.toLowerCase() ===
                permissionConstants.IMPORT_ACCESS.toLowerCase() &&
              p.action.toUpperCase() === POLICY_ACTIONS.ENABLED &&
              p.scope !== POLICY_SCOPES.NONE_OF_THE_RECORDS
          )
        );
        commit(
          "SET_EXPORT_ACCESS",
          policies.some(
            (p) =>
              p.resource.toLowerCase() ===
                permissionConstants.EXPORT_ACCESS.toLowerCase() &&
              p.action.toUpperCase() === POLICY_ACTIONS.ENABLED &&
              p.scope !== POLICY_SCOPES.NONE_OF_THE_RECORDS
          )
        );
        commit(
          "SET_BULK_DELETE_ACCESS",
          policies.some(
            (p) =>
              p.resource.toLowerCase() ===
                permissionConstants.BULK_DELETE.toLowerCase() &&
              p.action.toUpperCase() === POLICY_ACTIONS.ENABLED &&
              p.scope !== POLICY_SCOPES.NONE_OF_THE_RECORDS
          )
        );

        const enabledPermissions = [];

        permissionsSet.forEach(({ resource, hasAccess }) => {
          if (hasAccess) {
            enabledPermissions.push(resource);
          }
        });

        Object.entries(permissionConstants).forEach(([key, value]) => {
          if (value === permissionConstants.ALL) return;

          const hasEnabledAction = policies.some(
            (p) =>
              p.resource.toLowerCase() === value.toLowerCase() &&
              p.action.toUpperCase() === POLICY_ACTIONS.ENABLED &&
              p.scope !== POLICY_SCOPES.NONE_OF_THE_RECORDS
          );

          if (hasEnabledAction && !enabledPermissions.includes(value)) {
            enabledPermissions.push(value);
          }
        });

        const hasImport = policies.some(
          (p) =>
            p.resource.toLowerCase() ===
              permissionConstants.IMPORT_ACCESS.toLowerCase() &&
            p.action.toUpperCase() === POLICY_ACTIONS.ENABLED &&
            p.scope !== POLICY_SCOPES.NONE_OF_THE_RECORDS
        );
        if (
          hasImport &&
          !enabledPermissions.includes(permissionConstants.IMPORT_ACCESS)
        ) {
          enabledPermissions.push(permissionConstants.IMPORT_ACCESS);
        }

        const hasExport = policies.some(
          (p) =>
            p.resource.toLowerCase() ===
              permissionConstants.EXPORT_ACCESS.toLowerCase() &&
            p.action.toUpperCase() === POLICY_ACTIONS.ENABLED &&
            p.scope !== POLICY_SCOPES.NONE_OF_THE_RECORDS
        );
        if (
          hasExport &&
          !enabledPermissions.includes(permissionConstants.EXPORT_ACCESS)
        ) {
          enabledPermissions.push(permissionConstants.EXPORT_ACCESS);
        }

        localStorage.setItem(
          "userPermissions",
          JSON.stringify(enabledPermissions)
        );
      } catch (error) {
        console.error("[ABAC Store] Error fetching ABAC policies:", error);
        if (error.response?.status === 404) {
          console.warn(
            "⚠️ [ABAC Store] User has no role membership. Assign role in Permission Management."
          );
        }
      } finally {
        commit("SET_POLICIES_FETCHING", false);
      }
    },

    async fetchPermissions({ commit, dispatch }, options = {}) {
      try {
        if (isABACEnabled()) {
          await dispatch("fetchABACPolicies", options);
          return;
        }

        const permissionsData = localStorage.getItem("userPermissions");

        let permissions = [];

        if (permissionsData) {
          const parsed = JSON.parse(permissionsData);
          permissions = Array.isArray(parsed) ? parsed : [];
        }

        commit("SET_PERMISSIONS", permissions);
        commit("SET_ABAC_MODE", false);

        commit("SET_ALL_ACCESS", permissions.includes(permissionConstants.ALL));
        commit(
          "SET_USERS_ACCESS",
          permissions.includes(permissionConstants.USERS)
        );
        commit(
          "SET_BILLING_ACCESS",
          permissions.includes(permissionConstants.BILLING)
        );
        commit(
          "SET_INTEGRATIONS_ACCESS",
          permissions.includes(permissionConstants.INTEGRATIONS)
        );
        commit(
          "SET_OBJECTS_ACCESS",
          permissions.includes(permissionConstants.OBJECTS)
        );
        commit(
          "SET_CHATS_ACCESS",
          permissions.includes(permissionConstants.CHATS)
        );
        commit(
          "SET_CALL_SETTINGS_ACCESS",
          permissions.includes(permissionConstants.CALL_SETTINGS)
        );
        commit(
          "SET_EMAIL_TEMPLATES_ACCESS",
          permissions.includes(permissionConstants.EMAIL_TEMPLATES)
        );
        commit(
          "SET_DEAL_PIPELINE_ACCESS",
          permissions.includes(permissionConstants.DEAL_PIPELINE)
        );
        commit(
          "SET_SHARED_EMAILS_ACCESS",
          permissions.includes(permissionConstants.SHARED_EMAILS)
        );
        commit(
          "SET_SHARED_LINKEDIN_ACCESS",
          permissions.includes(permissionConstants.SHARED_LINKEDIN)
        );
        commit(
          "SET_IMPORT_ACCESS",
          permissions.includes(permissionConstants.IMPORT_ACCESS)
        );
        commit(
          "SET_EXPORT_ACCESS",
          permissions.includes(permissionConstants.EXPORT_ACCESS)
        );
        commit(
          "SET_CUSTOM_VIEWS_ACCESS",
          permissions.includes(permissionConstants.CUSTOM_VIEWS)
        );
        commit(
          "SET_PROSPECT_LEADS_ACCESS",
          permissions.includes(permissionConstants.PROSPECT_LEADS)
        );
        commit(
          "SET_PROSPECT_COMPANIES_ACCESS",
          permissions.includes(permissionConstants.PROSPECT_COMPANIES)
        );
        commit(
          "SET_AI_SDR_ACCESS",
          permissions.includes(permissionConstants.AI_SDR)
        );
        const hasSequencesAccess = permissions.includes(
          permissionConstants.SEQUENCES
        );
        commit("SET_SEQUENCES_ACCESS", hasSequencesAccess);
        commit(
          "SET_WORKFLOWS_ACCESS",
          permissions.includes(permissionConstants.WORKFLOWS)
        );
        commit("SET_HR_ATTENDANCE_ACCESS", permissions.includes(permissionConstants.HR_ATTENDANCE));
        commit("SET_HR_EMPLOYEE_MGMT_ACCESS", permissions.includes(permissionConstants.HR_EMPLOYEE_MGMT));
        commit("SET_HR_SHIFTS_ACCESS", permissions.includes(permissionConstants.HR_SHIFTS));
        commit("SET_HR_LEAVE_REQUESTS_ACCESS", permissions.includes(permissionConstants.HR_LEAVE_REQUESTS));
        commit("SET_HR_LEAVE_APPROVALS_ACCESS", permissions.includes(permissionConstants.HR_LEAVE_APPROVALS));
        commit("SET_HR_LEAVE_TYPES_ACCESS", permissions.includes(permissionConstants.HR_LEAVE_TYPES));
        commit("SET_HR_HOLIDAYS_ACCESS", permissions.includes(permissionConstants.HR_HOLIDAYS));
        commit("SET_HR_PERFORMANCE_ACCESS", permissions.includes(permissionConstants.HR_PERFORMANCE));
        commit("SET_HR_OKRS_ACCESS", permissions.includes(permissionConstants.HR_OKRS));
        commit("SET_HR_GOALS_ACCESS", permissions.includes(permissionConstants.HR_GOALS));
        commit("SET_HR_EMPLOYEES_ACCESS", permissions.includes(permissionConstants.HR_EMPLOYEES));
        commit("SET_HR_DEPARTMENTS_ACCESS", permissions.includes(permissionConstants.HR_DEPARTMENTS));
        commit("SET_HR_TEAMS_ACCESS", permissions.includes(permissionConstants.HR_TEAMS));
        commit("SET_HR_ROLES_ACCESS", permissions.includes(permissionConstants.HR_ROLES));
        commit("SET_HR_ONBOARDING_ACCESS", permissions.includes(permissionConstants.HR_ONBOARDING));
        commit("SET_HR_OFFBOARDING_ACCESS", permissions.includes(permissionConstants.HR_OFFBOARDING));
        commit("SET_HR_REQUISITIONS_ACCESS", permissions.includes(permissionConstants.HR_REQUISITIONS));
        commit("SET_HR_JOB_OPENINGS_ACCESS", permissions.includes(permissionConstants.HR_JOB_OPENINGS));
        commit("SET_HR_CANDIDATES_ACCESS", permissions.includes(permissionConstants.HR_CANDIDATES));
        commit("SET_HR_CAMPAIGNS_ACCESS", permissions.includes(permissionConstants.HR_CAMPAIGNS));
        commit(
          "SET_DIALER_ACCESS",
          permissions.includes(permissionConstants.DIALER)
        );
        commit(
          "SET_CONVERSATIONS_ACCESS",
          permissions.includes(permissionConstants.CONVERSATIONS)
        );
        commit(
          "SET_MEETINGS_ACCESS",
          permissions.includes(permissionConstants.MEETINGS)
        );
        commit(
          "SET_CONTACTS_ACCESS",
          permissions.includes(permissionConstants.CONTACTS)
        );
        commit(
          "SET_COMPANIES_ACCESS",
          permissions.includes(permissionConstants.COMPANIES)
        );
        commit(
          "SET_LEADS_ACCESS",
          permissions.includes(permissionConstants.LEADS)
        );
        commit(
          "SET_DEALS_ACCESS",
          permissions.includes(permissionConstants.DEALS)
        );
        commit(
          "SET_LISTS_ACCESS",
          permissions.includes(permissionConstants.LISTS)
        );
        commit(
          "SET_ANALYTICS_ACCESS",
          permissions.includes(permissionConstants.ANALYTICS)
        );
        commit(
          "SET_TASKS_ACCESS",
          permissions.includes(permissionConstants.TASKS)
        );
        commit(
          "SET_ALL_TASKS_ACCESS",
          permissions.includes(permissionConstants.ALL_TASKS)
        );
        commit(
          "SET_EMAILS_ACCESS",
          permissions.includes(permissionConstants.EMAILS)
        );
        commit(
          "SET_CAMPAIGNS_IN_ENGAGE_ACCESS",
          permissions.includes(permissionConstants.CAMPAIGNS_IN_ENGAGE)
        );
        commit(
          "SET_WATERFALL_ACCESS",
          permissions.includes(permissionConstants.WATERFALL)
        );
        commit(
          "SET_AGENT_BUILDER_ACCESS",
          permissions.includes(permissionConstants.AGENT_BUILDER)
        );
        commit(
          "SET_BULK_DELETE_ACCESS",
          permissions.includes(permissionConstants.BULK_DELETE)
        );
      } catch (error) {
        console.error("Error fetching permissions:", error);

        commit("SET_PERMISSIONS", []);
        commit("SET_ALL_ACCESS", false);
        commit("SET_USERS_ACCESS", false);
        commit("SET_BILLING_ACCESS", false);
        commit("SET_INTEGRATIONS_ACCESS", false);
        commit("SET_OBJECTS_ACCESS", false);
        commit("SET_CHATS_ACCESS", false);
        commit("SET_CALL_SETTINGS_ACCESS", false);
        commit("SET_EMAIL_TEMPLATES_ACCESS", false);
        commit("SET_DEAL_PIPELINE_ACCESS", false);
        commit("SET_SHARED_EMAILS_ACCESS", false);
        commit("SET_SHARED_LINKEDIN_ACCESS", false);
        commit("SET_IMPORT_ACCESS", false);
        commit("SET_EXPORT_ACCESS", false);
        commit("SET_CUSTOM_VIEWS_ACCESS", false);
        commit("SET_PROSPECT_LEADS_ACCESS", false);
        commit("SET_PROSPECT_COMPANIES_ACCESS", false);
        commit("SET_AI_SDR_ACCESS", false);
        commit("SET_SEQUENCES_ACCESS", false);
        commit("SET_WORKFLOWS_ACCESS", false);
        commit("SET_HR_ATTENDANCE_ACCESS", false);
        commit("SET_HR_EMPLOYEE_MGMT_ACCESS", false);
        commit("SET_HR_SHIFTS_ACCESS", false);
        commit("SET_HR_LEAVE_REQUESTS_ACCESS", false);
        commit("SET_HR_LEAVE_APPROVALS_ACCESS", false);
        commit("SET_HR_LEAVE_TYPES_ACCESS", false);
        commit("SET_HR_HOLIDAYS_ACCESS", false);
        commit("SET_HR_PERFORMANCE_ACCESS", false);
        commit("SET_HR_OKRS_ACCESS", false);
        commit("SET_HR_GOALS_ACCESS", false);
        commit("SET_HR_EMPLOYEES_ACCESS", false);
        commit("SET_HR_DEPARTMENTS_ACCESS", false);
        commit("SET_HR_TEAMS_ACCESS", false);
        commit("SET_HR_ROLES_ACCESS", false);
        commit("SET_HR_ONBOARDING_ACCESS", false);
        commit("SET_HR_OFFBOARDING_ACCESS", false);
        commit("SET_HR_REQUISITIONS_ACCESS", false);
        commit("SET_HR_JOB_OPENINGS_ACCESS", false);
        commit("SET_HR_CANDIDATES_ACCESS", false);
        commit("SET_HR_CAMPAIGNS_ACCESS", false);
        commit("SET_DIALER_ACCESS", false);
        commit("SET_CONVERSATIONS_ACCESS", false);
        commit("SET_MEETINGS_ACCESS", false);
        commit("SET_CONTACTS_ACCESS", false);
        commit("SET_COMPANIES_ACCESS", false);
        commit("SET_LEADS_ACCESS", false);
        commit("SET_DEALS_ACCESS", false);
        commit("SET_LISTS_ACCESS", false);
        commit("SET_ANALYTICS_ACCESS", false);
        commit("SET_TASKS_ACCESS", false);
        commit("SET_ALL_TASKS_ACCESS", false);
        commit("SET_EMAILS_ACCESS", false);
        commit("SET_CAMPAIGNS_IN_ENGAGE_ACCESS", false);
        commit("SET_WATERFALL_ACCESS", false);
        commit("SET_AGENT_BUILDER_ACCESS", false);
        commit("SET_BULK_DELETE_ACCESS", false);
      }
    },
  },
  getters: {
    allPermissions: (state) => state.permissions,
    hasAllAccess: (state) => state.hasAllAccess,
    hasChatsAccess: (state) => state.hasChatsAccess,
    hasImportAccess: (state) => state.hasImportAccess,
    hasExportAccess: (state) => state.hasExportAccess,
    hasProspectLeadsAccess: (state) => state.hasProspectLeadsAccess,
    hasProspectCompaniesAccess: (state) => state.hasProspectCompaniesAccess,
    hasAiSdrAccess: (state) => state.hasAiSdrAccess,
    hasSequencesAccess: (state) => state.hasSequencesAccess,
    hasWorkflowsAccess: (state) => state.hasWorkflowsAccess,
    hasHrAttendanceAccess: (state) => state.hasHrAttendanceAccess,
    hasHrEmployeeMgmtAccess: (state) => state.hasHrEmployeeMgmtAccess,
    hasHrShiftsAccess: (state) => state.hasHrShiftsAccess,
    hasHrLeaveRequestsAccess: (state) => state.hasHrLeaveRequestsAccess,
    hasHrLeaveApprovalsAccess: (state) => state.hasHrLeaveApprovalsAccess,
    hasHrLeaveTypesAccess: (state) => state.hasHrLeaveTypesAccess,
    hasHrHolidaysAccess: (state) => state.hasHrHolidaysAccess,
    hasHrPerformanceAccess: (state) => state.hasHrPerformanceAccess,
    hasHrOkrsAccess: (state) => state.hasHrOkrsAccess,
    hasHrGoalsAccess: (state) => state.hasHrGoalsAccess,
    hasHrEmployeesAccess: (state) => state.hasHrEmployeesAccess,
    hasHrDepartmentsAccess: (state) => state.hasHrDepartmentsAccess,
    hasHrTeamsAccess: (state) => state.hasHrTeamsAccess,
    hasHrRolesAccess: (state) => state.hasHrRolesAccess,
    hasHrOnboardingAccess: (state) => state.hasHrOnboardingAccess,
    hasHrOffboardingAccess: (state) => state.hasHrOffboardingAccess,
    hasHrRequisitionsAccess: (state) => state.hasHrRequisitionsAccess,
    hasHrJobOpeningsAccess: (state) => state.hasHrJobOpeningsAccess,
    hasHrCandidatesAccess: (state) => state.hasHrCandidatesAccess,
    hasHrCampaignsAccess: (state) => state.hasHrCampaignsAccess,
    hasDialerAccess: (state) => state.hasDialerAccess,
    hasMeetingsAccess: (state) => state.hasMeetingsAccess,
    hasContactsAccess: (state) => state.hasContactsAccess,
    hasCompaniesAccess: (state) => state.hasCompaniesAccess,
    hasLeadsAccess: (state) => state.hasLeadsAccess,
    hasDealsAccess: (state) => state.hasDealsAccess,
    hasListsAccess: (state) => state.hasListsAccess,
    hasAnalyticsAccess: (state) => state.hasAnalyticsAccess,
    hasTasksAccess: (state) => state.hasTasksAccess,
    hasEmailsAccess: (state) => state.hasEmailsAccess,
    hasCampaignsInEngageAccess: (state) => state.hasCampaignsInEngageAccess,
    hasWaterfallAccess: (state) => state.hasWaterfallAccess,
    hasAgentBuilderAccess: (state) => state.hasAgentBuilderAccess,
    hasBulkDeleteAccess: (state) => state.hasBulkDeleteAccess,
    hasPermission: (state) => (permission) =>
      state.permissions.includes(permission),
    userPolicies: (state) => state.userPolicies,
    isABACMode: (state) => state.isABACMode,
    isPoliciesFetching: (state) => state.isPoliciesFetching,
    canPerformAction: (state, _getters, rootState) => (resource, action) => {
      if (!state.isABACMode) {
        return state.permissions.includes(resource);
      }
      const policies = state.userPolicies || [];
      if (policies.length === 0) return false;
      const resourceLower = String(resource).toLowerCase();
      const actionUpper = String(action).toUpperCase();

      if (
        policies.some(
          (p) =>
            p.resource.toLowerCase() === resourceLower &&
            p.action.toUpperCase() === actionUpper &&
            p.scope !== "NONE_OF_THE_RECORDS",
        )
      ) {
        return true;
      }

      const objects = rootState?.objects?.objects || [];
      const matchingObject = objects.find((obj) => obj.slug === resourceLower);
      if (!matchingObject) return false;

      return policies.some(
        (p) =>
          p.resource === matchingObject.id &&
          p.action.toUpperCase() === actionUpper &&
          p.scope !== "NONE_OF_THE_RECORDS",
      );
    },

    canImport: (state, getters) => (resourceSlug) => {
      const globalImportAccess = state.hasImportAccess;

      if (!state.isABACMode) {
        return globalImportAccess;
      }

      const entityImportAccess = canPerformAction(
        resourceSlug,
        POLICY_ACTIONS.IMPORT
      );
      return globalImportAccess && entityImportAccess;
    },

    canExport: (state, getters) => (resourceSlug) => {
      const globalExportAccess = state.hasExportAccess;

      if (!state.isABACMode) {
        return globalExportAccess;
      }

      const entityExportAccess = canPerformAction(
        resourceSlug,
        POLICY_ACTIONS.EXPORT
      );
      return globalExportAccess && entityExportAccess;
    },

    canBulkDelete: (state, getters) => (resourceSlug) => {
      const globalBulkDeleteAccess = state.hasBulkDeleteAccess;

      if (!state.isABACMode) {
        return globalBulkDeleteAccess;
      }

      const entityDeleteAccess = canPerformAction(
        resourceSlug,
        POLICY_ACTIONS.DELETE
      );
      return globalBulkDeleteAccess && entityDeleteAccess;
    },
  },
};
